import { BehaviorSubject, catchError, debounceTime, distinctUntilChanged, EMPTY, filter, fromEvent, merge, Observable, of, Subject, switchMap, takeUntil, tap } from "rxjs"
import { ActiveFilters, IActiveFilters, IFilterRequest, IFilterResponse } from "../../../src/05_components/filter/dto/filter.dto"
import { Utils } from "../../../src/02_utils/utils"
import { ISearchRequest } from "../../../src/05_components/search/dto/requestBody.dto";
import { ErcasFilter } from "../../ercas-filter/filter";



export class SearchInputService {
  private elements: {
    filterForm: HTMLFormElement;
    inputs: NodeListOf<HTMLInputElement>;
    searchContainer: HTMLElement;
    searchInput: HTMLInputElement;
    searchOutputContainer: HTMLElement;
    searchOutput: HTMLElement;
    searchSuggestionDummy: HTMLElement;
    clearSearchElement: HTMLButtonElement;
    totalFitlerCounts: NodeListOf<HTMLElement>;
    filterButtons: NodeListOf<HTMLElement>;
    filterResetButtons: NodeListOf<HTMLElement>;
  };
  private cancelRequests: Subject<boolean> = new Subject();
  private filterCache = new Map<string, IActiveFilters>();

  constructor(private filterElement: HTMLElement, private $utils: Utils, private searchRequest: BehaviorSubject<ISearchRequest>, private error: Subject<Error>, private filters: {[key: string]: ErcasFilter}) {
    this.initElements();
    this.initInputEvents();

    this.searchRequest.subscribe(request => {
      this.updateFilters(request);
      this.fetchFilters().subscribe((activeFilters) => {
        this.restrictFilters(activeFilters);
      });

      this.$utils.addOrRemoveClases('add', 'd-none', this.elements.searchOutputContainer);
    });
  }

  private initInputEvents() {
    // Submit
    const submitEvents = merge(
      this.elements?.filterButtons ? fromEvent(this.elements.filterButtons, 'click') : EMPTY,
      this.elements?.searchInput ? fromEvent<KeyboardEvent>(this.elements.searchInput, 'keyup').pipe(filter((event) => event.key === 'Enter')) : EMPTY
    );

    submitEvents.subscribe(() => {
      const newRequestBody = this.getFilters();

      Object.keys(newRequestBody.nextPage)?.forEach(key => {
        const newFilterRequest: IFilterRequest = {
          ...this.filters[key].filterRequest.getValue(),
          nextPage: 1
        };

        this.filters[key].filterRequest.next(newFilterRequest);
      });

      this.searchRequest.next(newRequestBody);
    });

    // Reset
    fromEvent(this.elements.filterResetButtons, 'click').subscribe(() => {
      this.resetFilters();
    });

    // Input Change
    fromEvent(this.elements.inputs, 'change').pipe(
      switchMap(() =>
        this.fetchFilters()
      )
    ).subscribe((activeFilters) => {
      this.restrictFilters(activeFilters);
    });

    // Search Input
    merge(
      this.elements?.searchInput ? fromEvent(this.elements.searchInput, 'keyup') : EMPTY,
      this.elements?.searchInput ? fromEvent(this.elements.searchInput, 'focus') : EMPTY
    ).pipe(
      filter((x) => {
        if ((x.target as HTMLInputElement).value.length >= 3) {
          return true;
        }

        return false;
      }),
      switchMap(event => this.fetchFilters())
    ).subscribe(activeFilters => {
      this.searchSuggestions(activeFilters)
    });

    document.addEventListener('click', (event: Event) => {
      if (!this.elements.searchContainer?.contains(event.target as HTMLElement) && !this.elements.searchOutputContainer.classList.contains('d-none')) {
        this.cancelRequests.next(true);
        this.$utils.addOrRemoveClases('add', 'd-none', this.elements.searchOutputContainer);
      }
    }, {passive: true});
  }

  protected getFilters() {
    const currentFilters = new Map();

    this.elements.inputs?.forEach(input => {
      const name = input.dataset.name ?? (input.name ?? null);
      const value = input.dataset.value ?? (input.value ?? null);
      const type = input.dataset.type ??( input.type ?? null);

      //checkbox
      if (type == 'checkbox' || type == 'radio') {
        if (input.checked == true) {
          if (type == 'checkbox') {
            if (Array.isArray(currentFilters.get(name))) {
              const existing = currentFilters.get(name) ?? [];
              currentFilters.set(name, [...existing, value]);
            } else {
              currentFilters.set(name, [value]);
            }
          } else  {
            currentFilters.set(name, value);
          }
        } else if (!currentFilters.get(name)) {
          currentFilters.set(name, null);
        }
      } else if (type == 'text' || type == 'number') {
        currentFilters.set(name, (value ?? null));
      }
    });

    const newRequestBody: ISearchRequest = {
      ...this.searchRequest.getValue(),
      filters: Object.fromEntries(currentFilters)
    };

    return newRequestBody;
  }

  private resetFilters() {
    const newRequestBody: ISearchRequest = {
      ...this.searchRequest.getValue(),
      filters: {}
    };

    this.searchRequest.next(newRequestBody);
  }

  private async restrictFilters(activeFilters: IActiveFilters) {
    if (activeFilters) {
      this.elements.inputs?.forEach(input => {
        const type: string = input?.dataset.type ?? input?.type;
        if (type != 'checkbox' && type != 'radio') {
          return;
        }

        if (activeFilters?.filters[input.name]?.find(x => x == parseInt(input.value))) {
          input.disabled = false;
        } else {
          input.disabled = true;
          input.checked = false;
        }
      });

      this.elements.totalFitlerCounts?.forEach(totalFilterCount => {
        totalFilterCount.innerHTML = activeFilters.total?.toString();
      });
    }
  }

  protected updateFilters(request: ISearchRequest) {
    this.elements.inputs?.forEach(input => {
      const type: string = input?.dataset.type ?? input?.type;

      if (type == 'checkbox' || type == 'radio') {
        input.checked = false;
      } else {
        input.value = '';
      }
    });

    Object.keys(request.filters)?.forEach(key => {
      const value = request.filters[key];

      if (!value) {
        return;
      }

      if (Array.isArray(value)) {
        value.forEach(value => {
          this.updateInputs(value, key);
        });
        return;
      } else if (value) {
        this.updateInputs(value, key);
      }
    });
  }

  protected updateInputs(value: string, key: string) {
    const input = Array.from(this.elements.inputs).find(x => {
      if ((x.value == value || x.dataset.value == value || x.type == 'text') && (x.name == key || x.dataset.name == key)) {
        return x;
      }
    });

    const type: string = input?.dataset.type ?? input?.type;

    if (!input) {
      return;
    }

    if (type == 'checkbox' || type == 'radio') {
      input.checked = true;
    } else {
      input.value = value;
    }
  }

  private searchSuggestions(activeFilters: IActiveFilters) {
    this.clearSearchOutput();

    const suggestions = this.getSearchSuggestions(activeFilters, this.elements.searchInput?.name);

    if (!suggestions || suggestions.length === 0) {
      this.$utils.addOrRemoveClases('add', 'd-none', this.elements.searchOutputContainer);
      return;
    }

    this.$utils.addOrRemoveClases('remove', 'd-none', this.elements.searchOutputContainer);
    this.renderSuggestions(suggestions);
  }

  private clearSearchOutput(): void {
    this.elements.searchOutput.innerHTML = '';
  }

  private getSearchSuggestions(activeFilters: IActiveFilters, filterKey: string): Array<any> {
    return activeFilters?.filters?.[filterKey]?.filter(Boolean) ?? [];
  }

  private renderSuggestions(suggestions: string[]): void {
    suggestions.forEach((suggestionText) => {
      const suggestion = this.elements.searchSuggestionDummy.cloneNode(true) as HTMLElement;
      const button = suggestion.querySelector('button');

      if (button) {
        button.innerHTML = suggestionText;
        button.removeAttribute('aria-hidden');
        button.removeAttribute('tabindex');
        button.addEventListener('click', () => {
          this.applySuggestion(suggestionText);
        }, { passive: true });
      }

      this.$utils.addOrRemoveClases('remove', 'd-none', suggestion);
      this.elements.searchOutput.appendChild(suggestion);
    });
  }

  private applySuggestion(value: string): void {
    const newRequestBody = this.getFilters();
    newRequestBody.filters[this.elements.searchInput.name] = value;
    this.searchRequest.next(newRequestBody);
  }

  private fetchFilters(): Observable<IActiveFilters> {
    const requestBody = this.getFilters();
    const cacheKey = JSON.stringify(requestBody);

    if (this.filterCache.has(cacheKey)) {
      return of(this.filterCache.get(cacheKey));
    }

    return new Observable<IActiveFilters>((subscriber) => {
      const controller = new AbortController();
      const signal = controller.signal;

      fetch('/websitesearch/update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
        signal,
      })
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok) throw new Error(`${res.status} ${data.statusMessage}`);
        this.filterCache.set(cacheKey, data.data);
        subscriber.next(data.data);
        subscriber.complete();
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          subscriber.error(err);
        }
      });

      return () => controller.abort();
    }).pipe(
      takeUntil(this.cancelRequests.pipe(filter(Boolean))),
      catchError((error) => {
        this.error.next(error);
        return of(new ActiveFilters());
    }));
  }

  private initElements() {
    this.elements = {
      filterForm: this.filterElement.querySelector('form.js-filter-form'),
      inputs: this.filterElement.querySelectorAll('[data-input]'),
      searchContainer: this.filterElement.querySelector('[data-search-container]'),
      searchInput: this.filterElement.querySelector('[data-search]'),
      searchOutputContainer: this.filterElement.querySelector('[data-search-output-container]'),
      searchOutput: this.filterElement.querySelector('[data-search-output]'),
      searchSuggestionDummy: this.filterElement.querySelector('[data-search-dummy]'),
      clearSearchElement: this.filterElement.querySelector('[data-search-clear]'),
      totalFitlerCounts: this.filterElement.querySelectorAll('[data-total-filtered]'),
      filterButtons: this.filterElement.querySelectorAll('.js-filter-submit'),
      filterResetButtons: this.filterElement.querySelectorAll('.js-filter-reset')
    }
  }
}
